import React from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import ListItem
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem';
import ListItemContent
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/ListItemContent';
import classNames from 'classnames';
import ListItemLabel
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/ListItemLabel';
import FileChips
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/FileChips';
import CheckIcon from '@material-ui/icons/Check';
import ListItemActionsMenu
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/ListItemActionsMenu';
import useMobileLayout from 'Hooks/useMobileLayout';
import fr from 'facet-rest';
import ActionItemQuestionDialog
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/IHaveAQuestionButtonPicker/IHaveAQuestionButton/ActionItemQuestionDialog';
import ListItemActions
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/ListItemActions/ListItemActions';
import { useSnackbarContext } from 'Context/SnackbarContext';
import Sentry from 'Integrations/Sentry';

const useStyles = buildUseStyles('ActionItem', (theme => ({
  root: {},
  taskContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  taskDetail: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewAllTasksButton: {
    width: '100%',
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  chevronIcon: {
    marginRight: theme.spacing(1),
  },
})));

const ActionItem = ({
  resource,
  AlternativeForm,
  ...rest
}) => {
  // Menu anchor / open state
  const [anchorElement, setAnchorElement] = React.useState(null);
  const open = Boolean(anchorElement);

  const classes = useStyles();
  const isMobile = useMobileLayout();
  const { displayErrorMessage } = useSnackbarContext();
  const [openQuestionModal, setOpenQuestionModal] = React.useState(false);
  const [questionResource, setQuestionResource] = React.useState(null);

  const handleQuestionOnClick = async () => {
    resource.withRelationship(fr.relationships.question.name, async (relationship) => {
      try {
        // fetch collection
        const questions = await relationship.link('related').get();
        const newQuestion = await questions.create();
        setQuestionResource(newQuestion);

        // set to open modal
        setOpenQuestionModal(true);
      } catch (err) {
        Sentry.createSentryErrorLog(err);
        displayErrorMessage('Error loading question to planner');
      }
    }, () => {});
  };

  const handleQuestionModalOnClose = () => {
    setOpenQuestionModal(false);
    setQuestionResource(null);
  };

  const {
    expand,
    setExpand,
    actionItemOnClick,
    fileLinkCollection,
    fileExpand,
    completed,
    isAccountAi,
    isAccountRetitlingAi,
  } = rest;

  // Sets anchor element for the template menu
  const handleOptionsMenuClick = (event) => {
    setAnchorElement(event.currentTarget);
  };

  // Close Menu - Removes anchor element
  const handleCloseOptionsMenu = (event) => {
    event.stopPropagation();
    setAnchorElement(null);
  };

  // Action item paper click (mobile only)
  const handleActionItemOnClick = event => {
    if (isMobile) {
      handleOptionsMenuClick(event);
    }
  };

  return (
    <>
      <ListItem
        expand={expand}
        key={resource.id()}
        onClick={(e) => handleActionItemOnClick(e)}
      >
        <ListItemContent className={classNames(classes.taskContent)}>
          <Box className={classes.taskDetail}>
            <ListItemLabel
              resource={resource}
              expand={expand}
              setExpand={setExpand}
              className={classNames({ [classes.alignStart]: expand || fileExpand })}
            />

            {/* -------------- File Chips (Desktop) -------------- */}
            {(fileLinkCollection && !isMobile) ? (
              <FileChips
                classOverride={classes.fileChip}
                fileCollection={fileLinkCollection}
                {...rest}
              />
            ) : null}

            {/* -------------- Arrow right indicator to launch into account setup flows (Mobile) -------------- */}
            {(isMobile && (isAccountAi || isAccountRetitlingAi) && !completed) ? (
              <ListItemActionsMenu
                resource={resource}
                open={open}
                viewOptions={handleActionItemOnClick}
                anchorElement={anchorElement}
                onClose={handleCloseOptionsMenu}
                actionItemOnClick={actionItemOnClick}
                handleQuestionOnClick={handleQuestionOnClick}
                {...rest}
              />
            ) : null}

            {/* -------------- Show Completed Account Action item checkmark (Mobile) -------------- */}
            {(isMobile && (isAccountAi || isAccountRetitlingAi) && completed) ? (
              <CheckIcon
                className={classes.chevronIcon}
                color={'primary'}
              />
            ) : null}

            {/* -------------- Action Items options drawer (Mobile) -------------- */}
            {(isMobile && !isAccountAi && !isAccountRetitlingAi) ? (
              <ListItemActionsMenu
                resource={resource}
                open={open}
                anchorElement={anchorElement}
                onClose={handleCloseOptionsMenu}
                handleQuestionOnClick={handleQuestionOnClick}
                {...rest}
              />
            ) : null}

            {/* -------------- Action item options (Desktop) -------------- */}
            {!isMobile
              ? (
                <ListItemActions
                  resource={resource}
                  handleQuestionOnClick={handleQuestionOnClick}
                  {...rest}
                />
              ) : null
            }
          </Box>
          {AlternativeForm}
        </ListItemContent>
      </ListItem>
      <ActionItemQuestionDialog
        openModal={openQuestionModal}
        onClose={handleQuestionModalOnClose}
        resource={questionResource}
      />
    </>
  );
};

export default ActionItem;
